import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { ProfileChangeRequest } from '../../api/types';
import { approveChangeRequest, rejectChangeRequest } from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { formatIstDayMon } from '../../features/payments/paymentDates';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { StatusIcon } from '../ui/StatusIcon';
import { StatusPill } from '../ui/StatusPill';
import { ProfileMediaPreview } from './ProfileMediaPreview';
import {
  changeRequestErrorMessage,
  diffRows,
  isStaleDecision,
  maskAccount,
  sectionLabel,
  statusDisplay,
  type DiffRow,
  type ProfileValues,
} from './changeRequestDisplay';
import styles from './ProfileDocuments.module.css';

const NOT_PROVIDED = 'Not provided';

function safeDate(value: string | null | undefined): string | null {
  if (!value || Number.isNaN(new Date(value).getTime())) return null;
  return formatIstDayMon(value);
}

function Pill({ status }: { status: string | undefined }) {
  const d = statusDisplay(status);
  return (
    <StatusPill
      bg={d.bg}
      text={d.text}
      label={d.label}
      icon={d.icon ? <StatusIcon name={d.icon} /> : undefined}
    />
  );
}

function titleWord(value: string | undefined): string {
  const w = (value ?? '').replace(/_/g, ' ').trim().toLowerCase();
  return w ? w[0].toUpperCase() + w.slice(1) : 'unchanged';
}

/** One line of the Approve dialog: "<Field>: <now> → <requested>"; never an unmasked account number. */
function summaryLine(row: DiffRow): string {
  const now =
    row.now == null
      ? NOT_PROVIDED
      : row.isDocument
        ? 'current file'
        : row.isSecret
          ? (maskAccount(row.now) ?? NOT_PROVIDED)
          : row.now;
  const requested = row.isDocument
    ? 'new file'
    : row.isSecret
      ? (maskAccount(row.requested) ?? NOT_PROVIDED)
      : row.requested || NOT_PROVIDED;
  return `${row.label}: ${now} → ${requested}`;
}

/**
 * One PENDING change request as a before/after diff with Approve and Reject (#281). Approve shows
 * every change first (R25); Reject needs a reason of up to 200 characters (R26).
 */
export function ChangeRequestCard({
  userId,
  request,
  current,
  kycStatus,
}: {
  userId: number;
  request: ProfileChangeRequest;
  current: ProfileValues;
  kycStatus: string | undefined;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const label = sectionLabel(request.section);
  const rows = diffRows(request, current);
  const hasSecret = rows.some((r) => r.isSecret);
  const [revealed, setRevealed] = useState(false);
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = request.status === 'PENDING';
  const requested = safeDate(request.createdAt);

  const listKeys = [
    ['admin', 'users', userId, 'change-requests'],
    ['admin', 'change-requests'],
  ];
  // Decided elsewhere or gone: the list reloads when ops close the dialog, so the message stays
  // readable first (reloading at once would unmount this card and its open dialog).
  const [stale, setStale] = useState(false);
  const onFailure = (err: Error) => {
    setError(changeRequestErrorMessage(err));
    if (isStaleDecision(err)) setStale(true);
  };
  const close = () => {
    setError(null);
    setDialog(null);
    if (stale) {
      setStale(false);
      listKeys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
    }
  };
  const approve = useMutationHook(() => approveChangeRequest(request.id), {
    successMessage: `${label} change approved`,
    errorMessage: changeRequestErrorMessage,
    invalidateKeys: [
      ...listKeys,
      ['admin', 'users', 'overview', userId],
      ['admin', 'users', userId, 'verification-checks'],
    ],
    onSuccess: close,
    onError: onFailure,
  });
  const reject = useMutationHook((reason: string) => rejectChangeRequest(request.id, reason), {
    successMessage: `${label} change rejected`,
    errorMessage: changeRequestErrorMessage,
    invalidateKeys: [...listKeys, ['admin', 'users', userId, 'verification-checks']],
    onSuccess: close,
    onError: onFailure,
  });
  const sending = approve.isLoading || reject.isLoading;

  const value = (row: DiffRow, v: string | null, column: 'Now' | 'Requested') => {
    if (v == null || v === '') return <span className={styles.crEmpty}>{NOT_PROVIDED}</span>;
    if (row.isDocument)
      return (
        <ProfileMediaPreview
          key={v}
          userId={userId}
          url={v}
          label={`${column === 'Now' ? 'Current' : 'Requested'} ${row.label}`}
        />
      );
    if (row.isSecret && !revealed) return maskAccount(v);
    return v;
  };

  return (
    <section className={styles.changeRequest} aria-label={`Change request · ${label}`}>
      <div className={styles.crHeader}>
        <h5>{t('domain.changeRequestCard.heading', { label })}</h5>
        <Pill status={request.status} />
        {requested && <span className={styles.crMeta}>{t('domain.changeRequestCard.requestedOn', { date: requested })}</span>}
        {hasSecret && (
          <div className={`${styles.crControls} ${styles.crReveal}`}>
            <Button
              variant="ghost"
              size="sm"
              aria-label={revealed ? 'Hide account number' : 'Reveal account number'}
              onClick={() => setRevealed(!revealed)}
            >
              {revealed ? 'Hide' : 'Reveal'}
            </Button>
          </div>
        )}
      </div>
      {request.note && <p className={styles.crNote}>{request.note}</p>}
      <table className={styles.crTable}>
        <caption className={styles.srOnly}>{t('domain.changeRequestCard.caption', { label })}</caption>
        <thead>
          <tr>
            <th scope="col">{t('domain.changeRequestCard.fieldColumn')}</th>
            <th scope="col">{t('domain.changeRequestCard.nowColumn')}</th>
            <th scope="col">{t('domain.changeRequestCard.requestedColumn')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.label}</th>
              <td>
                {value(row, row.now, 'Now')}
                {row.whenAsked != null && (
                  <span className={styles.crWhenAsked}>
                    {t('domain.changeRequestCard.whenAsked')}{' '}
                    {row.isDocument
                      ? 'a different file'
                      : row.isSecret && !revealed
                        ? maskAccount(row.whenAsked)
                        : row.whenAsked}
                  </span>
                )}
              </td>
              <td>{value(row, row.requested, 'Requested')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {pending && (
        <div className={styles.crControls}>
          <Button variant="primary" disabled={sending} onClick={() => setDialog('approve')}>
            {t('domain.changeRequestCard.approve')}
          </Button>
          <Button variant="danger" disabled={sending} onClick={() => setDialog('reject')}>
            {t('domain.changeRequestCard.reject')}
          </Button>
        </div>
      )}
      <ConfirmDialog
        help="change-request"
        wide
        isOpen={dialog === 'approve'}
        title={`Approve ${label} change?`}
        description={
          <>
            <ul className={styles.crList}>
              {rows.map((row) => (
                <li key={row.key}>{summaryLine(row)}</li>
              ))}
            </ul>
            <p>{t('domain.changeRequestCard.approveNote', { status: titleWord(kycStatus) })}</p>
          </>
        }
        confirmText="Approve change"
        variant="success"
        loading={approve.isLoading}
        errorMessage={dialog === 'approve' ? error : null}
        onConfirm={() => {
          setError(null);
          approve.mutate(undefined);
        }}
        onCancel={close}
      />
      <ConfirmDialog
        help="change-request"
        isOpen={dialog === 'reject'}
        title={`Reject ${label} change?`}
        description="The user sees this reason on their profile."
        confirmText="Reject change"
        variant="danger"
        requireReason
        showReasonCounter
        reasonMinLength={1}
        reasonMaxLength={200}
        reasonPlaceholder="Why can't this change be approved? e.g. the GST certificate is not readable"
        loading={reject.isLoading}
        errorMessage={dialog === 'reject' ? error : null}
        onConfirm={(reason) => {
          if (!reason) return;
          setError(null);
          reject.mutate(reason);
        }}
        onCancel={close}
      />
    </section>
  );
}

const HISTORY_LIMIT = 3;

const decidedTime = (r: ProfileChangeRequest) => r.decidedAt ?? r.createdAt ?? '';

/** Decided requests as short lines, newest first: at most 3, then "Show all (n)". */
export function ChangeRequestHistory({ requests }: { requests: ProfileChangeRequest[] }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  if (requests.length === 0) return null;
  const sorted = [...requests].sort((a, b) => decidedTime(b).localeCompare(decidedTime(a)));
  const shown = all ? sorted : sorted.slice(0, HISTORY_LIMIT);
  return (
    <>
      <ul className={styles.crHistory} aria-label={t('domain.changeRequestHistory.listLabel')}>
        {shown.map((r) => {
          const date = safeDate(r.decidedAt);
          const meta = [
            sectionLabel(r.section),
            date ? `decided ${date}${r.decidedBy ? ` by ${r.decidedBy}` : ''}` : null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <li key={r.id}>
              <Pill status={r.status} />
              <span>{meta}</span>
              {r.status === 'REJECTED' && r.rejectionReason && (
                <span className={styles.crReason}>{t('domain.changeRequestHistory.reason', { reason: r.rejectionReason })}</span>
              )}
            </li>
          );
        })}
      </ul>
      {!all && sorted.length > HISTORY_LIMIT && (
        <div className={styles.crControls}>
          <Button variant="ghost" size="sm" onClick={() => setAll(true)}>
            {t('domain.changeRequestHistory.showAll', { n: sorted.length })}
          </Button>
        </div>
      )}
    </>
  );
}
